import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { IntlShape, useIntl } from 'react-intl';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router';
import { AuthContext } from '../../Auth/components/AuthProvider';
import {
  ApiResponse,
  ExternalIdDTO,
  Hierarchy,
  HierarchyApi,
  OrganisationApi,
  OrganisationDTO,
  OrganisationInstitutionalIdDTO,
  OrganisationOtherNameDTO,
  ReferencedOrganisationDTO,
  UrlDTO,
} from '../../api/core';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { Loading } from '../system/Loading';
import { Row } from './components/layout/Row';
import { renderDetailAccordionTab } from './components/layout/renderDetailAccordionTab';
import View from './components/layout/View';
import { Button } from 'primereact/button';
import { BreakingList } from './components/layout/BreakingList';
import { ExternalId } from './components/ExternalId';
import { InstitutionalId } from './components/InstitutionalId';
import { getNotation } from '../../Vocabulary/util';
import SearchResultsWork from '../dashboard/SearchResultsWork';
import SearchResultsPerson from '../dashboard/SearchResultsPerson';
import { deleteOrganisation, requestDeleteOrganisation } from '../../util/actions/organisation-actions';
import { InternalLink } from '../components/InternalLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons/faCaretRight';
import { localDate, localDateTime } from '../../util/intl.utils';
import { OrganisationRelationType } from '../../Settings/data';
import { boolFilter } from '../../Vocabulary/data';
import { Stamp } from '../components/Stamp';
import { UrlRender, UrlsRow } from './components/UrlsRow';
import { SubjectsRow } from './components/SubjectsRow';

export const DetailOrganisation = (): ReactNode => {
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();
  const navigate: NavigateFunction = useNavigate();
  const params: Readonly<Params<string>> = useParams();
  const {actions, authorizer} = useContext(AuthContext);
  const organisationApi: OrganisationApi = new OrganisationApi().withMiddleware(getAuthMiddleware(actions));
  const apiHierarchy: HierarchyApi = new HierarchyApi().withMiddleware(getAuthMiddleware(actions));

  const [organisation, setOrganisation] = useState<OrganisationDTO>();
  const [hierarchy, setHierarchy] = useState<Hierarchy[]>(null);


    useEffect(() => {
      if (params.id === undefined) {
        return void navigate('/search/organisations');
      }
      const id: number = Number(params.id);
      if (isNaN(id)) {
        toast.notify({ severity: 'error', summary: intl.formatMessage({ id: 'message.loading.failed.message' }) });
        return void navigate('/error/404');
      }
      if (!organisation || id !== organisation.id) {
        window.scrollTo(0, 0);
        void fetchData(id);
      }
    }, [params.id]);

  const fetchData = async (organisationId: number): Promise<void> => {
    try {
      const response: ApiResponse<OrganisationDTO> = await organisationApi.getReducedOrganisationRaw({organisationId});
      if (!response.raw.ok) {
        toast.notify({severity: 'error', summary: intl.formatMessage({id: 'message.loading.failed.message'})});
        return navigate(`/error/${response.raw.status}`);
      }
      setOrganisation(await response.value());

    } catch (error) {
      toast.notify({severity: 'error', summary: intl.formatMessage({id: 'message.loading.failed.message'})});
      return void navigate(`/error/${error?.status || 500}`);
    }
    const organisationHierarchyResponse: ApiResponse<Hierarchy[]> = await apiHierarchy.getOrganisationHierarchyRaw({organisationId});
    if (organisationHierarchyResponse.raw.ok) {
      setHierarchy(await organisationHierarchyResponse.value());
    } else {
      setHierarchy([]);
      toast.notify({
        summary: intl.formatMessage({id: 'error.loading.hierarchy.failed'}),
        severity: 'error',
        sticky: true,
      });
    }
  };

  if (!organisation) {
    return <Loading />;
  }

  // RENDER DATA FOR HEADER

  const renderOtherNames = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.otherNames || !organisationDTO.otherNames.length) return <></>;
    return <BreakingList>
      {organisationDTO.otherNames.map((otherName: OrganisationOtherNameDTO): string => otherName.otherName)}
    </BreakingList>;
  };

  const renderPeriod = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.startDate && !organisationDTO.endDate) return <></>;
    return <div>{`${localDate(organisationDTO.startDate, intl.locale)} - ${localDate(organisationDTO.endDate, intl.locale)}`}</div>;
  };

  const renderExternalIds = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.externalIds || !organisationDTO.externalIds.length) return <></>;
    return <BreakingList separator={''}>
      {(organisationDTO.externalIds || []).map((externalIdDTO: ExternalIdDTO, index: number): ReactNode =>
        <div key={index}><ExternalId id={externalIdDTO} entity={'orga'}/></div>,
      )}
    </BreakingList>;
  };

  const renderInstitutionalIds = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.organisationInstitutionalIds || !organisationDTO.organisationInstitutionalIds.length) return <></>;
    return <BreakingList separator={''}>
      {organisationDTO.organisationInstitutionalIds.map((institutionalId: OrganisationInstitutionalIdDTO, index: number): ReactNode =>
        <div key={index}><InstitutionalId id={institutionalId} entity={'orga'}/></div>,
      )}
    </BreakingList>;
  };

  const renderHierarchy = (hid: number, tree: Hierarchy[]): ReactNode => {
    if (!tree || !tree.length) return <></>;
    return <BreakingList separator={''}>
      {tree
        .sort((hierarchy1: Hierarchy, hierarchy2: Hierarchy): number => (hierarchy1.stage > hierarchy2.stage ? -1 : 1))
        .map((hierarchyItem: Hierarchy, index: number): ReactNode =>
          <div key={index} className={'flex gap-1 align-content-center'}>
            <span className={'inline md:hidden'} style={{width: `${0.5 * (index)}rem`}}></span>
            {index !== 0 && <span className={'text-400 text-xl'}><FontAwesomeIcon icon={faCaretRight}/></span>}
            {hid === hierarchyItem.entityId ? <span className={'flex align-items-center'}>{hierarchyItem.name}</span> :
              <InternalLink to={`/organisation/${hierarchyItem.entityId}`}>{hierarchyItem.name}</InternalLink>}
          </div>,
        )}
    </BreakingList>;
  };

  const renderRelation = (entities: ReferencedOrganisationDTO[], title: string): ReactNode => {
    return (
      <div>
        <strong>{title}: </strong>
        <BreakingList separator={', '}>
          {(entities || []).map((entity) => (
            <InternalLink to={`/organisation/${entity.id}`}>{entity.name}</InternalLink>
          ))}
        </BreakingList>
      </div>
    );
  };

  const renderPredecessors = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.predecessors || !organisationDTO.predecessors.length) return <></>;
    return (
      <Row title={getNotation('organisation_relation_type_list', OrganisationRelationType.PREDECESSOR, intl.locale)}>
        {(organisationDTO.predecessors || []).map((predecessor: ReferencedOrganisationDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div>
              <InternalLink to={`/organisation/${predecessor.id}`}>{predecessor.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${predecessor.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderSuccessors = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.successor || !organisationDTO.successor.length) return <></>;
    return (
      <Row title={getNotation('organisation_relation_type_list', OrganisationRelationType.SUCCESSOR, intl.locale)}>
        {(organisationDTO.successor || []).map((successor: ReferencedOrganisationDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div>
              <InternalLink to={`/organisation/${successor.id}`}>{successor.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${successor.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderParents = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.parents || !organisationDTO.parents.length) return <></>;
    return (
      <Row title={getNotation('organisation_relation_type_list', OrganisationRelationType.PARENT, intl.locale)}>
        {(organisationDTO.parents || []).map((parent: ReferencedOrganisationDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div>
              <InternalLink to={`/organisation/${parent.id}`}>{parent.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${parent.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderChilds = (organisationDTO: OrganisationDTO): ReactNode => {
    if (!organisationDTO.childs || !organisationDTO.childs.length) return <></>;
    return (
      <Row title={getNotation('organisation_relation_type_list', OrganisationRelationType.CHILD, intl.locale)}>
        {(organisationDTO.childs || []).map((child: ReferencedOrganisationDTO, index: number) => (
          <div key={index} className={'flex flex-row justify-content-between'}>
            <div>
              <InternalLink to={`/organisation/${child.id}`}>{child.name}</InternalLink>
            </div>
            <div>
              <small className={'white-space-nowrap'}>{`ID: ${child.id}`}</small>
            </div>
          </div>
        ))}
      </Row>
    );
  };

  const renderBasicData = (organisationDTO: OrganisationDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if(organisationDTO.name) {
      rows.push(<Row title={intl.formatMessage({id: 'organisation.attr.name.label'})}>{organisationDTO.name}</Row>);
    }
    if(organisationDTO.otherNames && organisationDTO.otherNames.length > 0) {
      rows.push(<Row title={intl.formatMessage({id: 'organisation.attr.otherNames.label'})}>{organisationDTO.otherNames.map((otherName: OrganisationOtherNameDTO): ReactNode => <div>{otherName.otherName}</div>)}</Row>);
    }
    if (organisationDTO.organisationInstitutionalIds && organisationDTO.organisationInstitutionalIds.length) {
      rows.push(<Row title={intl.formatMessage({id: 'organisation.attr.institutionalIds.label'})}>
        {organisationDTO.organisationInstitutionalIds.map((institutionalId: OrganisationInstitutionalIdDTO, index: number): ReactNode =>
          <div key={index}><InstitutionalId id={institutionalId} entity={'work'}/></div>,
        )}
      </Row>);
    }
    if (organisationDTO.externalIds && organisationDTO.externalIds.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'organisation.attr.externalIds.label' })}>
          {(organisationDTO.externalIds || []).map(
            (externalId: ExternalIdDTO, index: number): ReactNode => (
              <div key={index}>
                <ExternalId id={externalId} entity={'orga'} />
              </div>
            )
          )}
        </Row>
      );
    }
    if(organisationDTO.note && organisationDTO.note.length > 0) {
      rows.push(<Row title={intl.formatMessage({id: 'organisation.attr.note.label'})}><div>{organisationDTO.note}</div></Row>);
    }
    if(organisationDTO.startDate) {
      rows.push(<Row title={intl.formatMessage({id: 'organisation.attr.startDate.label'})}>{localDate(organisationDTO.startDate, intl.locale)}</Row>);
    }
    if(organisationDTO.endDate) {
      rows.push(<Row title={intl.formatMessage({id: 'organisation.attr.endDate.label'})}>{localDate(organisationDTO.endDate, intl.locale)}</Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'organisation.detail.section.basicData.title');
  };

  // RENDER DATA FOR DETAILS

  const renderRelationData = (organisationDTO: OrganisationDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (organisationDTO.predecessors && organisationDTO.predecessors.length) {
      rows.push(renderPredecessors(organisationDTO));
    }
    if (organisationDTO.successor && organisationDTO.successor.length) {
      rows.push(renderSuccessors(organisationDTO));
    }
    if (organisationDTO.parents && organisationDTO.parents.length) {
      rows.push(renderParents(organisationDTO));
    }
    if (organisationDTO.childs && organisationDTO.childs.length) {
      rows.push(renderChilds(organisationDTO));
    }
    return renderDetailAccordionTab(intl, rows, 'organisation.detail.section.relationData.title');
  };


  const renderContentData = (organisationDTO: OrganisationDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (organisationDTO.organisationSubjects && organisationDTO.organisationSubjects.length > 0) {
      rows.push(<SubjectsRow subjects={organisationDTO.organisationSubjects}/>);
    }
    if (organisationDTO.urls && organisationDTO.urls.length > 0) {
      rows.push(
        <UrlsRow
          title={intl.formatMessage({ id: 'organisation.attr.urls.label' })}
          urls={(organisationDTO.urls || []).map(
            (url: UrlDTO): UrlRender => ({ url: url.url, type: getNotation('url_list', url.type, intl.locale) })
          )}
        />
      );
    }
    return renderDetailAccordionTab(intl, rows, 'organisation.detail.section.additionalInformation.title');
  };

  const renderAdminData = (organisationDTO: OrganisationDTO): ReactNode => {
    const rows: ReactNode[] = [];
    rows.push(<Row title={intl.formatMessage({id: 'organisation.attr.id.label'})}>{organisationDTO.id}</Row>);
    if(organisationDTO.adminDataOrganisation.origin) {
      rows.push(<Row
        title={intl.formatMessage({id: 'organisation.attr.origin.label'})}>{organisationDTO.adminDataOrganisation.origin}</Row>);
    }
    if(organisationDTO.adminDataOrganisation.changed) {
      rows.push(<Row
        title={intl.formatMessage({id: 'organisation.attr.changed.label'})}>{localDateTime(organisationDTO.adminDataOrganisation.changed, intl.locale)}</Row>);
    }
    if(organisationDTO.adminDataOrganisation.created) {
      rows.push(<Row
        title={intl.formatMessage({id: 'organisation.attr.created.label'})}>{localDateTime(organisationDTO.adminDataOrganisation.created, intl.locale)}</Row>);
    }
    if(organisationDTO.adminDataOrganisation.deskman) {
      rows.push(<Row
        title={intl.formatMessage({id: 'organisation.attr.deskman.label'})}>{organisationDTO.adminDataOrganisation.deskman}</Row>);
    }
    if(organisationDTO.adminDataOrganisation.editorialStatus) {
      rows.push(<Row
        title={intl.formatMessage({id: 'organisation.attr.editorialStatus.label'})}>{getNotation('editorial_status_list', organisationDTO.adminDataOrganisation.editorialStatus, intl.locale)}</Row>);
    }
    if(organisationDTO.adminDataOrganisation.hidden) {
      rows.push(<Row
        title={intl.formatMessage({id: 'organisation.attr.hidden.label'})}>{boolFilter(organisationDTO.adminDataOrganisation.hidden || false)}</Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'organisation.detail.section.adminData.title');
  };

  return (
    <View
      title={organisation.name}
      header={
        <>
          {renderOtherNames(organisation)}
          {renderPeriod(organisation)}
          {renderExternalIds(organisation)}
          {renderInstitutionalIds(organisation)}
          {renderHierarchy(organisation.id, hierarchy)}
          {organisation.parents?.length > 0 &&
            renderRelation(organisation.parents, intl.formatMessage({ id: 'organisation.attr.parents.label' }))}
          {organisation.childs?.length > 0 &&
            renderRelation(organisation.childs, intl.formatMessage({ id: 'organisation.attr.childs.label' }))}
          {organisation.successor?.length > 0 &&
            renderRelation(organisation.successor, intl.formatMessage({ id: 'organisation.attr.successors.label' }))}
          {organisation.predecessors?.length > 0 &&
            renderRelation(
              organisation.predecessors,
              intl.formatMessage({ id: 'organisation.attr.predecessors.label' })
            )}
        </>
      }
      toolbar={{
        left: [],
        center: [
          organisation.adminDataOrganisation.hidden && <Stamp red={true}>{intl.formatMessage({ id: 'organisation.attr.hidden.label' })}</Stamp>],
        right: [
          authorizer.hasRoleOrHigher('ROLE_EDITOR') && (
            <Button
              icon={'pi pi-pencil'}
              onClick={(): void | Promise<void> => navigate(`/organisation/${organisation.id}/edit`)}
              title={intl.formatMessage({ id: 'button.edit' })}
            />
          ),
          authorizer.hasRole('ROLE_ADMIN') && (
            <Button
              icon={'pi pi-trash'}
              severity={'danger'}
              onClick={(): void =>
                deleteOrganisation(organisationApi, toast, intl, navigate, organisation.id, organisation.name)
              }
              title={intl.formatMessage({ id: 'button.delete' })}
            />
          ),
          authorizer.hasRole('ROLE_EDITOR') && (
            <Button
              icon={'pi pi-trash'}
              severity={'warning'}
              onClick={(): void =>
                requestDeleteOrganisation(organisationApi, toast, intl, navigate, organisation.id, organisation.name)
              }
              title={intl.formatMessage({ id: 'button.deleteRequest' })}
            />
          ),
        ],
      }}
      details={[
        renderBasicData(organisation),
        renderRelationData(organisation),
        renderContentData(organisation),
        renderAdminData(organisation),
      ]}
      tabs={[
        {
          title: intl.formatMessage({ id: 'entity.works' }),
          content: (
            <SearchResultsWork
              externalParams={{ queryString: `organisation_id_belongs_to:${organisation.id}` }}
            ></SearchResultsWork>
          ),
        },
        {
          title: `${intl.formatMessage({ id: 'entity.works' })} (${intl.formatMessage({ id: 'embeddedDash.aboutPeople' })})`,
          content: (
            <SearchResultsWork
              externalParams={{ queryString: `organisation_id_persons:${organisation.id}` }}
            ></SearchResultsWork>
          ),
        },
        {
          title: `${intl.formatMessage({ id: 'entity.works' })} (${intl.formatMessage({ id: 'embeddedDash.aboutPeopleIncludingChilds' })})`,
          content: (
            <SearchResultsWork
              externalParams={{ queryString: `organisation_id_recursive:${organisation.id}` }}
            ></SearchResultsWork>
          ),
        },
        {
          title: intl.formatMessage({ id: 'entity.persons' }),
          content: (
            <SearchResultsPerson externalParams={{ queryString: `orga_id:${organisation.id}` }}></SearchResultsPerson>
          ),
        },
        {
          title: `${intl.formatMessage({ id: 'entity.persons' })} (${intl.formatMessage({ id: 'embeddedDash.includingChilds' })})`,
          content: (
            <SearchResultsPerson
              externalParams={{ queryString: `orga_id_recursive:${organisation.id}` }}
            ></SearchResultsPerson>
          ),
        },
      ]}
    />
  );
};

export default DetailOrganisation;
